import React, { useState } from "react";
import {
  alpha,
  Box,
  Divider,
  InputBase,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { useTranslation } from "react-i18next";
/**
 * SelectionMenu
 * @param {HTMLElement} anchorEl - Element to which the menu is anchored.
 * @param {boolean} open - Whether the menu is open.
 * @param {function} onClose - Function to close the menu.
 * @param {Array<{ value: any, label: string }>} options - Menu options.
 * @param {any} selectedValue - Currently selected value.
 * @param {function} onSelect - Function called when an option is selected.
 * @param {boolean} showCustomInput - Determines whether to display the input field for entering the year.
*/

// Main selection menu
export const SelectionMenu = ({
  anchorEl,
  open,
  onClose,
  options,
  selectedValue,
  onSelect,
  showCustomInput = false,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("report");
  const [customYear, setCustomYear] = useState("");
  const isValidYear = /^\d{4}$/.test(customYear);
  
  const handleCustomSubmit = () => {
    if (!isValidYear) return;
    onSelect(+customYear);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: `drop-shadow(0px 2px 8px ${alpha(
              theme.palette.common.black,
              0.25
            )})`,
            mt: 1,
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 10,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <Box sx={{ maxHeight: "50vh", overflowY: "auto", p: 0.5 }}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={selectedValue === option.value}
            onClick={() => {
              onSelect(option.value);
              onClose();
            }}
            sx={{
              borderRadius: 2,
              m: 0.25,
              border: `1px solid ${theme.palette.blueGray50.main}`,
            }}
          >
            <Typography
              color={selectedValue === option.value ? "primary" : "secondary"}
              variant="h6"
            >
              {option.label}
            </Typography>
          </MenuItem>
        ))}

        {showCustomInput && (
          <>
            <Divider sx={{ my: 1 }} />
            {/* Item for custom year */}
            <MenuItem
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 2,
                py: 0.5,
                pr: 1,
                border: `1px solid ${theme.palette.blueGray50.main}`,
              }}
            >
              <InputBase
                type="number"
                placeholder={`${t("Filter.another_year")}...`}
                value={customYear}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,4}$/.test(value)) {
                    setCustomYear(value);
                  }
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCustomSubmit();
                  }
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 4,
                }}
                sx={{
                  flex: 1,
                  maxWidth: 44,
                  fontSize: 12.5,
                  fontWeight: 600,
                  p: 0,
                  m: 0,
                  "& input::placeholder": {
                    fontSize: 9,
                    opacity: 0.6,
                  },
                }}
                onFocus={(e) => e.stopPropagation()}
              />
              <EGIconButton
                disabled={!isValidYear}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCustomSubmit();
                }}
                iconSize={12}
                title={t("Filter.search_year")}
                iconName="search2"
                iconColor="secondary"
              />
            </MenuItem>
          </>
        )}
      </Box>
    </Menu>
  );
};
